import type { FurniturePart, PartRole, Vec2 } from "../types";

/**
 * Small geometry helpers shared by the parametric templates. All values are
 * footprint-local inches: origin at the footprint center, `+x` to the right,
 * `+y` toward the front of the piece.
 */

/** Axis-aligned rectangle outline from its top-left corner and size. */
export function rect(x: number, y: number, w: number, h: number): Vec2[] {
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
  ];
}

/** Axis-aligned rectangle outline centered at `(cx, cy)`. */
export function rectC(cx: number, cy: number, w: number, h: number): Vec2[] {
  return rect(cx - w / 2, cy - h / 2, w, h);
}

/** Polygonal ellipse. `n` is chosen from the radius so curves stay smooth at
 *  large sizes without wasting vertices on tiny ones. */
export function ellipse(cx: number, cy: number, rx: number, ry: number, n?: number): Vec2[] {
  // Always a multiple of 4 so vertices land exactly on the ±rx / ±ry
  // extremes and the polygon's bbox equals the true ellipse bbox.
  const raw = n ?? Math.max(24, Math.min(96, Math.round((rx + ry) * 1.2)));
  const segs = Math.max(8, Math.round(raw / 4) * 4);
  const out: Vec2[] = [];
  for (let i = 0; i < segs; i++) {
    const t = (i / segs) * Math.PI * 2;
    out.push({ x: cx + Math.cos(t) * rx, y: cy + Math.sin(t) * ry });
  }
  return out;
}

export function circle(cx: number, cy: number, r: number, n?: number): Vec2[] {
  return ellipse(cx, cy, r, r, n);
}

/**
 * An organic "blob" — a circle whose radius wobbles with a couple of low
 * frequency harmonics. Deterministic for a given `seed` so the same plant
 * always looks the same.
 */
export function blob(cx: number, cy: number, r: number, seed = 1, n = 48): Vec2[] {
  const out: Vec2[] = [];
  const a1 = 0.12 + ((seed * 37) % 7) * 0.01;
  const a2 = 0.07 + ((seed * 53) % 5) * 0.01;
  const p1 = seed * 1.7;
  const p2 = seed * 2.9;
  // Normalize so the wobble never exceeds `r` (keeps parts inside the footprint).
  const norm = 1 / (1 + a1 + a2);
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const rr = r * norm * (1 + a1 * Math.sin(3 * t + p1) + a2 * Math.sin(5 * t + p2));
    out.push({ x: cx + Math.cos(t) * rr, y: cy + Math.sin(t) * rr });
  }
  return out;
}

/** Stadium / pill shape (rectangle with fully rounded short ends). */
export function pill(cx: number, cy: number, w: number, h: number): Vec2[] {
  if (w <= h) return ellipse(cx, cy, w / 2, h / 2);
  const r = h / 2;
  const half = w / 2 - r;
  const out: Vec2[] = [];
  const segs = 16;
  for (let i = 0; i <= segs; i++) {
    const t = -Math.PI / 2 + (i / segs) * Math.PI;
    out.push({ x: cx + half + Math.cos(t) * r, y: cy + Math.sin(t) * r });
  }
  for (let i = 0; i <= segs; i++) {
    const t = Math.PI / 2 + (i / segs) * Math.PI;
    out.push({ x: cx - half + Math.cos(t) * r, y: cy + Math.sin(t) * r });
  }
  return out;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Translate an outline. */
export function translate(pts: Vec2[], dx: number, dy: number): Vec2[] {
  return pts.map((p) => ({ x: p.x + dx, y: p.y + dy }));
}

/** Rotate an outline by `deg` (clockwise in screen space) around `(cx, cy)`. */
export function rotate(pts: Vec2[], deg: number, cx = 0, cy = 0): Vec2[] {
  const rad = (deg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return pts.map((p) => {
    const x = p.x - cx;
    const y = p.y - cy;
    return { x: cx + x * c - y * s, y: cy + x * s + y * c };
  });
}

/** Mirror an outline across the vertical axis (x → -x). */
export function mirrorX(pts: Vec2[]): Vec2[] {
  return pts.map((p) => ({ x: -p.x, y: p.y }));
}

let partCounter = 0;

/** Build a part with a stable-ish id. Ids only need to be unique within one
 *  piece; templates rebuild deterministically so we reset per build. */
export function part(
  role: PartRole,
  outline: Vec2[],
  z0: number,
  z1: number,
  extra: Partial<Omit<FurniturePart, "id" | "role" | "outline" | "z0" | "z1">> = {}
): FurniturePart {
  partCounter += 1;
  return {
    id: `${role}-${partCounter}`,
    role,
    outline,
    z0,
    z1,
    ...extra,
  };
}

/** Reset the part id counter — call at the start of every template build. */
export function resetPartIds() {
  partCounter = 0;
}

/** A seam segment. */
export function seam(ax: number, ay: number, bx: number, by: number) {
  return { a: { x: ax, y: ay }, b: { x: bx, y: by } };
}

/** Evenly divide a span into `n` cells with `gap` between them. Returns the
 *  start coordinate and size of each cell. */
export function divide(start: number, length: number, n: number, gap: number) {
  const count = Math.max(1, Math.round(n));
  const cell = (length - gap * (count - 1)) / count;
  const out: { start: number; size: number }[] = [];
  for (let i = 0; i < count; i++) {
    out.push({ start: start + i * (cell + gap), size: cell });
  }
  return out;
}
